<template>
    <div class="e-penalty-main-market">
        <div class="e-penalty-main-market-titles">
            <div
                v-for="market in markets"
                v-bind:key="market.marketType"
                class="e-penalty-main-market-title"
            >
                {{ market.title }}
            </div>
        </div>
        <div class="e-penalty-main-market-columns">
            <div
                v-for="market in markets"
                v-bind:key="market.marketType"
                class="e-penalty-main-market-column"
            >
                <div class="e-penalty-main-market-option-type">
                    <div
                        v-for="row in market.layout.labels"
                        v-bind:key="row.key"
                        class="e-penalty-main-market-option-type-cell"
                    >
                        {{ row.label }}
                    </div>
                </div>
                <div
                    v-for="(group, groupIndex) in market.layout.groups"
                    v-bind:key="groupIndex"
                    class="e-penalty-main-market-odds-group"
                >
                    <div
                        v-for="(cell, cellIndex) in group"
                        v-bind:key="cellIndex"
                        class="e-penalty-main-market-odds-cell"
                    >
                        <Odds
                            v-if="cell"
                            v-bind:oddsInfo="cell.odds"
                            v-bind:priceOption="cell.priceOption"
                            v-bind:matchInfo="match"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { type PropType, computed } from 'vue';
    import Odds from '@/components/match/Odds.vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { IOdds, IPriceOption, MainMarketOdds } from '@/interface/IOdds';
    import { MarketType } from '@/interface/enum';
    import { getBetOptionNameForMainMarket, getMainMarketTitle } from '@/libraries/helpers/betOptionDisplayHelper';

    interface IOddsCell {
        odds: IOdds;
        priceOption: IPriceOption;
    }

    const props = defineProps({
        match: {
            type: Object as PropType<IMatch>,
            required: true,
        },
        mainMarketOdds: {
            type: Array as PropType<MainMarketOdds[]>,
            required: true,
        },
        isExpanded: {
            type: Boolean,
            default: false,
        },
    });

    const markets = computed(() => props.mainMarketOdds
        .filter(market => market.marketType !== MarketType.Unknown)
        .map(market => ({
            marketType: market.marketType,
            title: getMainMarketTitle(props.match, market.betTypeGroups[0], market.marketType),
            layout: toColumns(market),
        })));

    function toColumns(market: MainMarketOdds) {
        const labelOdds = market.rawOddsList[0];
        if (!labelOdds) return { labels: [], groups: [] };

        const lineGroups = props.isExpanded ? market.oddsList : market.oddsList.slice(0, 1);
        const rows = lineGroups.flatMap((lines, linesIndex) => labelOdds.priceOption.map(priceOption => ({
            key: `${linesIndex}-${priceOption.option}`,
            label: getBetOptionNameForMainMarket(props.match, labelOdds, priceOption),
            cells: lines.map(odds => toCell(odds, priceOption.option)),
        })));

        const groupCount = Math.max(...rows.map(row => row.cells.length), 0);
        const groups = Array.from(
            { length: groupCount },
            (_, groupIndex) => rows.map(row => row.cells[groupIndex] ?? null),
        );

        return { labels: rows, groups };
    }

    function toCell(odds: IOdds | null, option: string): IOddsCell | null {
        const priceOption = odds?.priceOption.find(x => x.option === option);
        return odds && priceOption ? { odds, priceOption } : null;
    }
</script>

<style lang="scss" scoped>
@import "@/scss/components/odd";
@import "@/scss/components/ePenalty/ePenaltyMainMarket";
</style>
